import { Injectable, signal, computed } from '@angular/core';

export interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class PaginationService {
  private _page = signal(0);
  private _pageSize = signal(10);
  private _total = signal(0);

  readonly page = this._page.asReadonly();
  readonly pageSize = this._pageSize.asReadonly();
  readonly total = this._total.asReadonly();

  readonly totalPages = computed(() => Math.ceil(this._total() / this._pageSize()));

  readonly hasNextPage = computed(() => this._page() < this.totalPages() - 1);
  readonly hasPreviousPage = computed(() => this._page() > 0);

  setPage(page: number): void {
    this._page.set(Math.max(0, page));
  }

  goToPage(page: number): void {
    this.setPage(page);
  }

  nextPage(): void {
    if (this.hasNextPage()) {
      this._page.update(p => p + 1);
    }
  }

  previousPage(): void {
    if (this.hasPreviousPage()) {
      this._page.update(p => p - 1);
    }
  }

  setPageSize(size: number): void {
    this._pageSize.set(size);
    this._page.set(0);
  }

  setTotal(total: number): void {
    this._total.set(total);
    if (this._page() >= this.totalPages() && this._page() > 0) {
      this._page.set(0);
    }
  }

  reset(): void {
    this._page.set(0);
    this._pageSize.set(10);
    this._total.set(0);
  }

  getStartIndex(): number {
    return this._page() * this._pageSize();
  }

  getEndIndex(): number {
    return Math.min(this.getStartIndex() + this._pageSize(), this._total());
  }
}
